import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import {
	Organization,
	OrganizationResponse,
	OrgConfig,
	OrgCourseConfig,
	OrgCourseCustomizationSingle,
	OrgCourseCustomizationsList,
	OrgCourseVersionKey,
	OrgCustomizedCoursesList,
	OrgProductLine
} from "@data/organization";
import { ServiceResponse } from "@data/response";
import { API } from "@service/system/api.service";
import { HttpManager } from "@service/system/httpManager.service";
import { ResponseHandler } from "@service/system/responseHandler.service";

const API_URL = API.API_URL;

@Injectable()
export class OrganizationsService {
	private _organizationURL = API_URL + "/organization";
	private _organizationsURL = API_URL + "/organizations";

	constructor(private httpManager: HttpManager, private responseHandler: ResponseHandler) {}

	getOrganizations(simple = false): Observable<ServiceResponse<Organization[]>> {
		return this.responseHandler.httpGet<Organization[]>(`${this._organizationsURL}`, { simple: simple });
	}

	getOrganizationByName(searchTextName: string | string[]): Observable<ServiceResponse<Organization[]>> {
		if (searchTextName.length < 1) {
			return this.responseHandler.httpGet<Organization[]>(`${this._organizationsURL}`);
		} else {
			return this.responseHandler.httpGet<Organization[]>(`${this._organizationsURL}/find`, {
				name: searchTextName
			});
		}
	}

	getOrganizationById(searchTextID: { length: number }): Observable<ServiceResponse<Organization[]>> {
		if (searchTextID.length > 0) {
			return this.responseHandler.httpGet<Organization[]>(`${this._organizationsURL}/${searchTextID}`);
		} else {
			return this.responseHandler.httpGet<Organization[]>(`${this._organizationsURL}`);
		}
	}

	getOrgConfig(orgID = ""): Observable<ServiceResponse<OrgConfig[]>> {
		return this.responseHandler.httpGet<OrgConfig[]>(`${this._organizationsURL}/${orgID}/config`);
	}

	GetOrgCVKeys(
		orgID: { length: number },
		detailed: string | string[]
	): Observable<ServiceResponse<OrgCourseVersionKey[]>> {
		if (orgID.length > 0) {
			return this.responseHandler.httpGet<OrgCourseVersionKey[]>(`${this._organizationsURL}/${orgID}/keys`, {
				detailed: detailed
			});
		}
	}

	getOrgCourseLicenseKey(
		orgID: { length: number },
		courseID: string | string[]
	): Observable<ServiceResponse<OrgCourseVersionKey[]>> {
		if (orgID.length > 0 && courseID != "") {
			return this.responseHandler.httpGet<OrgCourseVersionKey[]>(
				`${this._organizationsURL}/${orgID}/keys/${courseID}`
			);
		}
	}

	getCustomCoursesList(orgID: string): Observable<ServiceResponse<OrgCustomizedCoursesList[]>> {
		if (orgID.length > 0) {
			return this.responseHandler.httpGet<OrgCustomizedCoursesList[]>(
				`${this._organizationsURL}/${orgID}/courses`
			);
		}
	}

	getCourseConfigByOrg(orgID: any, cvItemID: number): Observable<ServiceResponse<OrgCourseConfig[]>> {
		return this.responseHandler.httpGet<OrgCourseConfig[]>(`${this._organizationsURL}/${orgID}/config/${cvItemID}`);
	}

	getCustomizationPagesByOrg(
		orgID: string,
		cvItemID: string
	): Observable<ServiceResponse<OrgCourseCustomizationSingle[]>> {
		return this.responseHandler.httpGet<OrgCourseCustomizationSingle[]>(
			`${this._organizationsURL}/${orgID}/courses/${cvItemID}/pages`
		);
	}

	getOrgCourseCustomizationsList(
		orgID: string,
		cvItemID: string
	): Observable<ServiceResponse<OrgCourseCustomizationsList[]>> {
		if (orgID.length > 0 && cvItemID.length > 0) {
			return this.responseHandler.httpGet<OrgCourseCustomizationsList[]>(
				`${this._organizationsURL}/${orgID}/courses/${cvItemID}?activeOnly=true`
			);
		}
	}

	getOrgProductLinesByString(searchString: string | string[]): Observable<ServiceResponse<OrgProductLine[]>> {
		searchString = searchString == null ? "" : searchString;
		return this.responseHandler.httpGet<OrgProductLine[]>(`${API_URL}/productlines/orgs`, {
			searchString: searchString
		});
	}

	setOrganization(body: string): Observable<ServiceResponse<OrganizationResponse>> {
		return this.responseHandler.httpPost<OrganizationResponse>(`${this._organizationURL}`, body);
	}

	addCrsCustomization(body: any): Observable<ServiceResponse<OrganizationResponse>> {
		return this.responseHandler.httpPost<OrganizationResponse>(`${this._organizationsURL}/set/customization`, body);
	}

	updateCrsConfiguration(body: string): Observable<ServiceResponse<OrganizationResponse>> {
		return this.responseHandler.httpPost<OrganizationResponse>(`${this._organizationsURL}/set/courseconfig`, body);
	}

	updateCrsCustomization(body: string): Observable<ServiceResponse<OrganizationResponse>> {
		return this.responseHandler.httpPost<OrganizationResponse>(
			`${this._organizationsURL}/set/coursecustomization`,
			body
		);
	}

	updateOrg(body: string): Observable<ServiceResponse<OrganizationResponse>> {
		return this.responseHandler.httpPost<OrganizationResponse>(`${this._organizationsURL}/set/config`, body);
	}
}
